import Button from 'devextreme-react/button';
import DropDownButton from 'devextreme-react/drop-down-button';
import type { FileReviewPanelPlacement } from '@jarvis/platform-file-review';
import { useMemo, useState } from 'react';
import { useShellLayout } from '../shell/useShellLayout';

export interface PdfViewerToolbarActionsProps {
  showOverlays: boolean;
  onToggleOverlays: () => void;
  chatOpen: boolean;
  onToggleChat: () => void;
  placement: FileReviewPanelPlacement;
  onDiscussionPlacementChange: (placement: FileReviewPanelPlacement) => void;
  onDownload: () => void;
  onShare: () => void;
  disabled?: boolean;
}

interface PdfDiscussionPositionButtonProps {
  value: FileReviewPanelPlacement;
  disabled?: boolean;
  /** When true, show a text label next to the icon (preview ribbon). */
  showText?: boolean;
  onChange: (placement: FileReviewPanelPlacement) => void;
}

export function PdfDiscussionPositionButton({
  value,
  disabled = false,
  showText = false,
  onChange
}: PdfDiscussionPositionButtonProps) {
  const [open, setOpen] = useState(false);

  function choose(next: FileReviewPanelPlacement) {
    onChange(next);
    setOpen(false);
  }

  return (
    <span className="jarvis-pdf-position-button">
      <Button
        icon="preferences"
        text={showText ? 'Panel position' : undefined}
        hint="Discussion panel position"
        stylingMode="text"
        disabled={disabled}
        elementAttr={{ 'aria-label': 'Discussion panel position' }}
        onClick={() => setOpen((visible) => !visible)}
      />
      {open && !disabled ? (
        <div className="jarvis-pdf-position-button__popup" role="dialog" aria-label="Discussion panel position">
          <p className="jarvis-pdf-position-button__title">Discussion panel</p>
          {(['left', 'right', 'off'] as FileReviewPanelPlacement[]).map((placement) => (
            <label key={placement} className="jarvis-pdf-position-button__option">
              <input
                type="radio"
                name="jarvis-pdf-discussion-position"
                value={placement}
                checked={value === placement}
                onChange={() => choose(placement)}
              />
              <span>{placement === 'off' ? 'Off' : placement === 'left' ? 'Left' : 'Right'}</span>
            </label>
          ))}
        </div>
      ) : null}
    </span>
  );
}

const PLACEMENT_MENU_IDS = {
  left: 'placement-left',
  right: 'placement-right',
  off: 'placement-off'
} as const;

export function PdfViewerToolbarActions({
  showOverlays,
  onToggleOverlays,
  chatOpen,
  onToggleChat,
  placement,
  onDiscussionPlacementChange,
  onDownload,
  onShare,
  disabled = false
}: PdfViewerToolbarActionsProps) {
  const { isNarrow } = useShellLayout();

  const menuItems = useMemo(
    () => [
      {
        id: 'highlights',
        text: showOverlays ? 'Hide highlights' : 'Show highlights',
        icon: showOverlays ? 'eyeopen' : 'eyeclose',
        disabled
      },
      { id: PLACEMENT_MENU_IDS.left, text: 'Panel on left', icon: 'chevronleft', disabled },
      { id: PLACEMENT_MENU_IDS.right, text: 'Panel on right', icon: 'chevronright', disabled },
      { id: PLACEMENT_MENU_IDS.off, text: 'Panel off', icon: 'remove', disabled },
      { id: 'download', text: 'Download PDF', icon: 'download', disabled },
      { id: 'share', text: 'Share link', icon: 'share', disabled }
    ],
    [disabled, showOverlays]
  );

  const menuHandlers = useMemo(
    () =>
      new Map<string, () => void>([
        ['highlights', onToggleOverlays],
        [PLACEMENT_MENU_IDS.left, () => onDiscussionPlacementChange('left')],
        [PLACEMENT_MENU_IDS.right, () => onDiscussionPlacementChange('right')],
        [PLACEMENT_MENU_IDS.off, () => onDiscussionPlacementChange('off')],
        ['download', onDownload],
        ['share', onShare]
      ]),
    [onDiscussionPlacementChange, onDownload, onShare, onToggleOverlays]
  );

  if (isNarrow) {
    return (
      <DropDownButton
        className="jarvis-pdf-viewer__toolbar-menu"
        icon="menu"
        stylingMode="outlined"
        hint="PDF actions"
        disabled={disabled}
        displayExpr="text"
        keyExpr="id"
        dropDownOptions={{ width: 240, showTitle: false }}
        items={menuItems}
        onItemClick={(e) => {
          const id = e.itemData?.id;
          if (typeof id === 'string') menuHandlers.get(id)?.();
        }}
      />
    );
  }

  return (
    <>
      <Button
        icon={showOverlays ? 'eyeopen' : 'eyeclose'}
        hint={showOverlays ? 'Hide highlights' : 'Show highlights'}
        stylingMode="text"
        disabled={disabled}
        elementAttr={{ 'aria-label': showOverlays ? 'Hide highlights' : 'Show highlights' }}
        onClick={onToggleOverlays}
      />
      <Button
        icon="comment"
        hint={chatOpen ? 'Hide comments' : 'Show comments'}
        stylingMode="text"
        disabled={disabled}
        elementAttr={{ 'aria-label': chatOpen ? 'Hide comments' : 'Show comments' }}
        onClick={onToggleChat}
      />
      <PdfDiscussionPositionButton
        value={placement}
        disabled={disabled}
        onChange={onDiscussionPlacementChange}
      />
      <Button
        icon="download"
        stylingMode="text"
        hint="Download PDF"
        disabled={disabled}
        elementAttr={{ 'aria-label': 'Download PDF' }}
        onClick={onDownload}
      />
      <Button
        icon="share"
        stylingMode="text"
        hint="Share link to PDF viewer"
        disabled={disabled}
        elementAttr={{ 'aria-label': 'Share link to PDF viewer' }}
        onClick={onShare}
      />
    </>
  );
}
